import { ResultCode } from "../../../../lib/result-codes";
import {
  MERCHANT_ONBOARDING_STAGES,
  type MerchantOnboardingStage,
} from "../../shared/types/merchant-onboarding.types";

export const MERCHANT_REGISTRATION_PATHS = {
  account: "/merchant/register/account",
  verifyEmail: "/merchant/register/verify-email",
  emailVerified: "/merchant/register/email-verified",
  businessDetails: "/merchant/register/business-details",
} as const;

export const getMerchantOnboardingStage = (
  resultCode: number,
): MerchantOnboardingStage | undefined => {
  switch (resultCode) {
    case ResultCode.AuthEmailNotVerified:
      return MERCHANT_ONBOARDING_STAGES.verifyEmail;
    case ResultCode.LocalMerchantNotFound:
      return MERCHANT_ONBOARDING_STAGES.businessDetails;
    default:
      return undefined;
  }
};

export const getMerchantOnboardingPath = (stage?: MerchantOnboardingStage): string => {
  switch (stage) {
    case MERCHANT_ONBOARDING_STAGES.verifyEmail:
      return MERCHANT_REGISTRATION_PATHS.verifyEmail;
    case MERCHANT_ONBOARDING_STAGES.completed:
      return "/merchant/home";
    default:
      return MERCHANT_REGISTRATION_PATHS.businessDetails;
  }
};

interface MerchantRegistrationAccess {
  isLoggedIn: boolean;
  role?: string;
  requestedPath: string;
  onboardingStage?: MerchantOnboardingStage;
}

export const hasMissingMerchantOnboardingStage = (
  role: string | undefined,
  onboardingStage?: MerchantOnboardingStage,
) => role === "merchant" && !onboardingStage;

/** Returns the canonical redirect for a merchant registration route, or null when allowed. */
export const getMerchantRegistrationRedirect = ({
  isLoggedIn,
  role,
  requestedPath,
  onboardingStage,
}: MerchantRegistrationAccess): string | null => {
  if (!isLoggedIn) {
    return requestedPath === MERCHANT_REGISTRATION_PATHS.account
      ? null
      : MERCHANT_REGISTRATION_PATHS.account;
  }

  if (role !== "merchant") return "/dashboard";
  if (onboardingStage === MERCHANT_ONBOARDING_STAGES.completed) return "/merchant/home";

  const nextPath = getMerchantOnboardingPath(onboardingStage);
  if (onboardingStage === MERCHANT_ONBOARDING_STAGES.verifyEmail) {
    return requestedPath === nextPath ? null : nextPath;
  }

  const isAllowedBusinessStep =
    requestedPath === MERCHANT_REGISTRATION_PATHS.businessDetails ||
    requestedPath === MERCHANT_REGISTRATION_PATHS.emailVerified;

  return isAllowedBusinessStep ? null : nextPath;
};

export const getMerchantDashboardRedirect = (
  role: string | undefined,
  onboardingStage?: MerchantOnboardingStage,
): string | null => {
  if (role !== "merchant" || onboardingStage === MERCHANT_ONBOARDING_STAGES.completed) return null;
  if (hasMissingMerchantOnboardingStage(role, onboardingStage)) return "/login";
  return getMerchantOnboardingPath(onboardingStage);
};
